"use client";

import React from "react";
import { motion } from "framer-motion";
import { LogIn, Mail, Lock, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function LoginFormComponent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="flex flex-col items-center justify-center py-8 text-center space-y-4">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="text-xl font-default font-semibold text-foreground">
          ログイン成功
        </h3>
        <p className="text-muted-foreground font-sans">
          {successMessage}
        </p>
        <Button asChild className="mt-6 w-full" variant="outline">
          <Link to="/HomePage">ダッシュボードへ進む</Link>
        </Button>
      </motion.div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      {submitError && <Alert variant="destructive" className="mb-6">
          <AlertCircle className="size-4" />
          <AlertTitle className="font-default">エラー</AlertTitle>
          <AlertDescription className="font-sans">{submitError}</AlertDescription>
        </Alert>}

      <FormField control={control} name="username" rules={{
      required: "メールアドレスまたはユーザー名を入力してください。"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground">メールアドレスまたはユーザー名</FormLabel>
            <FormControl>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                  <Mail className="size-4" />
                </div>
                <Input placeholder="example@msbiotech.com" className="pl-10 font-sans bg-background/50 focus-visible:bg-background transition-colors" {...field} />
              </div>
            </FormControl>
            <FormMessage className="font-sans" />
          </FormItem>} />

      <FormField control={control} name="password" rules={{
      required: "パスワードを入力してください。",
      minLength: {
        value: 6,
        message: "パスワードは6文字以上である必要があります。"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel className="font-sans text-foreground">パスワード</FormLabel>
              <Link to="#" className="text-sm font-sans text-primary hover:underline">
                パスワードをお忘れですか？
              </Link>
            </div>
            <FormControl>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                  <Lock className="size-4" />
                </div>
                <Input type="password" placeholder="••••••••" className="pl-10 font-sans bg-background/50 focus-visible:bg-background transition-colors" {...field} />
              </div>
            </FormControl>
            <FormMessage className="font-sans" />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full font-sans text-base h-11 mt-2 shadow-md hover:shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            認証中...
          </> : <>
            <LogIn className="mr-2 size-5" />
            ログイン
          </>}
      </Button>
    </Form>;
}
export default function MemberLogin() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="memberlogin" className="relative min-h-[80vh] flex items-center justify-center bg-background py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" ref={ref}>
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/40 blur-[100px]" />
        <div className="absolute top-[20%] right-[10%] w-[20%] h-[20%] rounded-lg bg-chart-4/5 blur-[80px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }}>
          <Card className="border-border/40 shadow-xl bg-card/80 backdrop-blur-xl overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-chart-4 to-primary opacity-80" />
            
            <CardHeader className="space-y-2 text-center pb-6 pt-8">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-2 shadow-sm">
                <LogIn className="size-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-default font-bold tracking-tight text-foreground">
                メンバーログイン
              </CardTitle>
              <CardDescription className="font-sans text-muted-foreground">
                アカウントにアクセスするにはログインしてください。
              </CardDescription>
            </CardHeader>

            <CardContent>
              <FormProvider formId="member-login-form" sectionName="MemberLogin" defaultValues={{
              username: "",
              password: ""
            }} validationRules={{
              username: {
                required: true
              },
              password: {
                required: true,
                minLength: 6
              }
            }} submitText="ログイン" successMessage="ログインに成功しました。リダイレクトしています..." errorMessage="ログインに失敗しました。入力内容をご確認ください。">
                <LoginFormComponent />
              </FormProvider>
            </CardContent>

            <CardFooter className="flex justify-center border-t border-border/40 pt-6 pb-8 bg-muted/20">
              <p className="text-sm text-muted-foreground font-sans">
                アカウントをお持ちでないですか？{" "}
                <Link to="#" className="text-primary font-medium hover:underline transition-colors">
                  新規登録はこちら
                </Link>
              </p>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </section>;
}